import { DppPassport, DppProduct } from "@keyban/sdk-base";

import { useKeybanApplication } from "~/application";
import { useKeybanNft } from "~/nft";
import { usePromise } from "~/promise";
import { useKeybanClient } from "~/provider";

/**
 * Retrives a single product.
 * @param productId - The identifier of the product
 * @returns - The result containing the product or an error.
 */
export function useKeybanProduct(
  productId: string,
): ReturnType<typeof usePromise<DppProduct, true>> {
  const client = useKeybanClient();

  return usePromise(
    `dpp:product:${productId}`,
    () => client.api.dpp.getProduct(productId),
    { suspense: true },
  );
}

/**
 * Retrives a single product.
 * @param passportId - The identifier of the product
 * @returns - The result containing the passport or an error.
 */
export function useKeybanPassport(
  passportId: string,
): ReturnType<typeof usePromise<DppPassport, true>> {
  const client = useKeybanClient();

  return usePromise(
    `dpp:passport:${passportId}`,
    () => client.api.dpp.getPassport(passportId),
    { suspense: true },
  );
}

/**
 * Retrives a single passport token.
 * @param passportId - The ID of the passport
 * @returns - The result containing the passport token or an error.
 */
export function useKeybanPassportToken(passportId: string) {
  const { network } = useKeybanClient();

  const [app, appError] = useKeybanApplication();
  const tokenAddress = app?.dppSettings.tokens.find(
    (dppToken) => dppToken.network === network,
  )?.address;

  const [tokenId, tokenIdError] = usePromise(
    ["dpp", passportId].join(":"),
    async () => {
      const buf = await crypto.subtle.digest(
        "SHA-256",
        new TextEncoder().encode(passportId),
      );

      const h = "0123456789ABCDEF".toLowerCase();
      const hex = [...new Uint8Array(buf)]
        .flatMap((v) => h[v >> 4] + h[v & 15])
        .join("");

      return BigInt(`0x${hex}`).toString();
    },
    { suspense: true },
  );

  const [nft, nftError] = useKeybanNft(tokenAddress ?? "", tokenId ?? "");

  return [nft, appError ?? tokenIdError ?? nftError] as const;
}
